import { useEffect, useRef, useState, memo } from 'react';
import { message as toast, ScrollView, IScrollViewContext } from '@/sub-luca-plugin/compatible-components';
import CollectAction from '@/sub-luca-plugin/components/collect-action';
import QaSkeleton from '../qa-skeleton';
import QaRecords, { IQaRecordItem } from '@/sub-luca-plugin/components/qa-render/qa-records';
import { toastInQaDetail } from '@/sub-luca-plugin/utils/qa';
import { qa_detail_by_ask_id, qa_pair_detail } from '@/sub-luca-plugin/apis';
import { transformToQaRecordsByOldQaDetail, transformToQaRecordsByPairOfQaDetail } from '@/sub-luca-plugin/utils/qa';
import NavigationHeaderWapper from '@/sub-luca-plugin/components/navigation-header-wapper';
import { TOnCollectStatusChange } from '@/sub-luca-plugin/types';
import { getFlashHeight } from './helper';
import './index.scss';

interface IProps {
    // 数据类型
    type: 'gbot_detail' | 'qa_pair_detail';
    id: string | number;
    // 报错时是否需要使用Qa组件内的toast组件
    needToastInQa?: boolean;
    onClose?: () => void;
    onCollectStatusChange?: TOnCollectStatusChange;
}

const QuestionDetail = (props: IProps) => {
    const { type, id, needToastInQa, onClose } = props;
    const containerRef = useRef<HTMLDivElement | null>(null);
    const scrollDom = useRef<IScrollViewContext>();
    const qaFlashIndex = useRef(0);
    const [detail, setDetail] = useState<{
        projectSubjectName?: string;
        summary?: string;
        initState?: boolean;
    }>();
    const [records, setRecords] = useState<IQaRecordItem[]>([]);

    const handleInitError = (message: string) => {
        needToastInQa ? toastInQaDetail(message) : toast.warning(message);
        setTimeout(() => onClose?.(), 500);
    };

    const initPairOfQaDetail = async (qaId: string) => {
        const { result, message = '网络开了点儿小差～' } = await qa_pair_detail({ qaId });
        if (!result) {
            handleInitError(message);
            return;
        }
        const { flashIndex, records } = transformToQaRecordsByPairOfQaDetail(result);
        qaFlashIndex.current = flashIndex;
        setDetail({ initState: result.collectFlag });
        setRecords(records);
    };

    const initGbotQaDetail = async (askId: number) => {
        const { result, message = '网络开了点儿小差～' } = await qa_detail_by_ask_id(askId);
        if (!result) {
            handleInitError(message);
            return;
        }
        const { askCollect, projectName, subjectName, answer } = result;
        setDetail({
            initState: askCollect?.is_ask_collect === 1,
            projectSubjectName: `${projectName}-${subjectName}`,
            summary: answer?.[0]?.question_content,
        });
        setRecords(transformToQaRecordsByOldQaDetail(result));
    };

    const handleScrollToFlashData = async () => {
        if (!qaFlashIndex.current) return;
        const top = await getFlashHeight(qaFlashIndex.current, containerRef.current!);
        scrollDom.current?.scrollTo({ top });
    };

    const handleClose = () => {
        onClose?.();
        setTimeout(() => {
            setRecords([]);
            setDetail(undefined);
        }, 500);
    };

    useEffect(() => {
        if (records.length) {
            type === 'gbot_detail' ? setTimeout(() => scrollDom.current?.scrollToBottom(), 100) : handleScrollToFlashData();
        }
    }, [records]);

    useEffect(() => {
        if (!id) return;
        if (type === 'qa_pair_detail') {
            initPairOfQaDetail(id as string);
        } else {
            initGbotQaDetail(id as number);
        }
    }, [id, type]);

    return detail ? (
        <div className="qa-old-question" ref={containerRef}>
            <NavigationHeaderWapper onClose={handleClose}>
                <div className="qa-old-question__title">问题详情</div>
                <CollectAction initState={detail.initState} type={type} id={id} needToastInQa onChange={props.onCollectStatusChange} />
            </NavigationHeaderWapper>
            {detail.projectSubjectName && (
                <div className="question-info-box">
                    <span>{detail.projectSubjectName}</span>
                    <span>{detail.summary}</span>
                </div>
            )}
            <ScrollView className="old-qa-records-container" ref={scrollDom} scrollY>
                <QaRecords list={records} toastInQa={toastInQaDetail} />
            </ScrollView>
        </div>
    ) : (
        <QaSkeleton />
    );
};

export default memo(QuestionDetail);
